import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BASE_PATH as GOODS_BASE_PATH } from '@app/api/goods-service/variables';
import { GoodsSkuControllerService } from '@app/api/goods-service/api/goodsSkuController.service';
import { BASE_PATH as STOCK_BASE_PATH } from '@app/api/stock-service/variables';
import { GoodsStockControllerService } from '@app/api/stock-service/api/goodsStockController.service';
import { PackageOperationControllerService } from '@app/api/stock-service/api/packageOperationController.service';
import { PackageOperationVO } from '@app/api/stock-service/model/packageOperationVO';
import { PackageStockResultVO } from '@app/api/stock-service/model/packageStockResultVO';
import { environment } from '@environments/environment';
import { PermissionService } from '@app/core/services/permission.service';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  PaginationEvent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import {
  FormFieldConfig,
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { openAdvancedTableColumnSelector } from '@shared/utils/advanced-table-column-selector.util';
import {
  PACKAGE_INVENTORY_COLUMNS,
  PACKAGE_INVENTORY_SEARCH_CONFIG,
  PackageInventoryItem,
} from './package-operation.config';
import {
  PackageInventoryFilters,
  PackageOperationService,
} from './package-operation.service';
import { PackageOperationFormDialogComponent } from './components/package-operation-form-dialog/package-operation-form-dialog.component';
import { PackageOperationHistoryDrawerComponent } from './components/package-operation-history-drawer/package-operation-history-drawer.component';
import {
  OperationPreviewItem,
  OperationWarehouseOption,
  PackageOperationForm,
  PackageOperationType,
} from './components/package-operation.models';

@Component({
  selector: 'app-package-operation',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ToastModule,
    TranslateModule,
    AppButtonComponent,
    AdvancedDataTableComponent,
    SearchFormComponent,
    PackageOperationHistoryDrawerComponent,
    PackageOperationFormDialogComponent,
  ],
  providers: [
    MessageService,
    { provide: GOODS_BASE_PATH, useValue: environment.goodsServiceBasePath },
    { provide: STOCK_BASE_PATH, useValue: environment.stockURL },
    GoodsSkuControllerService,
    GoodsStockControllerService,
    PackageOperationControllerService,
    PackageOperationService,
  ],
  templateUrl: './package-operation.component.html',
  styleUrl: './package-operation.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PackageOperationComponent {
  @ViewChild('inventoryTable')
  private readonly inventoryTable?: AdvancedDataTableComponent;

  private readonly service = inject(PackageOperationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  readonly searchConfig: FormFieldConfig[] = PACKAGE_INVENTORY_SEARCH_CONFIG;
  readonly inventoryColumns: TableColumn[] = PACKAGE_INVENTORY_COLUMNS;
  readonly inventoryItems = signal<PackageInventoryItem[]>([]);
  readonly inventoryLoading = signal(false);
  readonly inventoryTotalRecords = signal(0);
  readonly inventoryPage = signal({ page: 1, size: 20 });
  readonly selectedPackage = signal<PackageInventoryItem | null>(null);
  readonly historyVisible = signal(false);
  readonly historyItems = signal<PackageOperationVO[]>([]);
  readonly historyLoading = signal(false);
  readonly canViewHistory = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'inventory.package_sku_operations.view_history',
    );
  });
  readonly canPack = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.package_sku_operations.pack');
  });
  readonly canUnpack = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.package_sku_operations.unpack');
  });
  readonly operationVisible = signal(false);
  readonly operationType = signal<PackageOperationType | null>(null);
  readonly operationPackage = signal<PackageInventoryItem | null>(null);
  readonly operationWarehouseOptions = signal<OperationWarehouseOption[]>([]);
  readonly sourcePacks = signal<PackageOperationVO[]>([]);
  readonly operationPreviewItems = signal<OperationPreviewItem[]>([]);
  readonly operationStockEnough = signal<boolean | null>(null);
  readonly operationValidationMessage = signal<string | null>(null);
  readonly operationStockChecking = signal(false);
  readonly operationSubmitting = signal(false);
  readonly sourcePacksLoading = signal(false);
  readonly operationForm: PackageOperationForm = new FormGroup({
    warehouseId: new FormControl<number | null>(null, Validators.required),
    packageQty: new FormControl<number | null>(1, [Validators.required, Validators.min(1)]),
    reason: new FormControl<string>(''),
    sourcePackOperationId: new FormControl<number | null>(null),
  });
  private stockCheckRequestId = 0;
  private currentFilters: PackageInventoryFilters = {};

  constructor() {
    this.loadInventory();
  }

  onSearch(searchData: SearchFormData): void {
    this.currentFilters = {
      skuCode: this.toOptionalString(searchData['skuCode']),
      skuName: this.toOptionalString(searchData['skuName']),
    };
    this.inventoryPage.set({ ...this.inventoryPage(), page: 1 });
    this.loadInventory();
  }

  onReset(): void {
    this.currentFilters = {};
    this.inventoryPage.set({ page: 1, size: 20 });
    this.loadInventory();
  }

  onInventoryPageChange(event: PaginationEvent): void {
    const rows = event.rows ?? this.inventoryPage().size;
    this.inventoryPage.set({
      page: Math.floor((event.first ?? 0) / rows) + 1,
      size: rows,
    });
    this.loadInventory();
  }

  onColumnConfigClick(event: Event): void {
    if (this.inventoryTable) {
      openAdvancedTableColumnSelector(this.inventoryTable, event);
    }
  }

  openHistory(item: PackageInventoryItem): void {
    this.selectedPackage.set(item);
    this.historyItems.set([]);
    this.historyVisible.set(true);
    this.loadHistory();
  }

  openOperation(type: PackageOperationType, item: PackageInventoryItem): void {
    if ((type === 'PACK' && !this.canPack()) || (type === 'UNPACK' && !this.canUnpack())) {
      return;
    }

    this.operationType.set(type);
    this.operationPackage.set(item);
    this.operationPreviewItems.set([]);
    this.operationStockEnough.set(null);
    this.operationValidationMessage.set(null);
    this.sourcePacks.set([]);
    this.operationForm.reset({
      warehouseId: null,
      packageQty: 1,
      reason: '',
      sourcePackOperationId: null,
    });
    this.operationVisible.set(true);
    this.loadOperationWarehouses(item);
  }

  onOperationWarehouseChange(): void {
    this.operationForm.controls.sourcePackOperationId.setValue(null);
    this.sourcePacks.set([]);
    this.operationPreviewItems.set([]);
    this.operationStockEnough.set(null);
    this.operationValidationMessage.set(null);

    if (this.operationType() === 'UNPACK') {
      this.loadEligibleSourcePacks();
    }
    this.checkOperationStock();
  }

  onOperationQuantityChange(): void {
    this.checkOperationStock();
  }

  onSourcePackChange(): void {
    this.checkOperationStock();
  }

  submitOperation(): void {
    const type = this.operationType();
    const packageSku = this.operationPackage();
    const warehouseId = this.operationForm.controls.warehouseId.value;
    const packageQty = this.operationForm.controls.packageQty.value;
    const sourcePackOperationId = this.operationForm.controls.sourcePackOperationId.value;

    this.operationForm.markAllAsTouched();
    if (
      !type ||
      !packageSku ||
      !warehouseId ||
      !this.isWholePositiveQuantity(packageQty) ||
      this.operationStockEnough() !== true ||
      (type === 'UNPACK' && !sourcePackOperationId)
    ) {
      return;
    }

    const confirmedPackageQty = Number(packageQty);
    const confirmedSourcePackOperationId = Number(sourcePackOperationId);
    const reason = this.operationForm.controls.reason.value?.trim() || undefined;
    const request = {
      operationNo: this.createOperationNo(type),
      packageSkuCode: packageSku.skuCode,
      packageQty: confirmedPackageQty,
      warehouseId,
      reason,
    };
    const operation$ =
      type === 'PACK'
        ? this.service.pack(request)
        : this.service.unpack({
            ...request,
            sourcePackOperationId: confirmedSourcePackOperationId,
          });

    this.operationSubmitting.set(true);
    operation$.subscribe({
      next: () => {
        this.operationSubmitting.set(false);
        this.operationVisible.set(false);
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant('package_operation.messages.operation_completed'),
        });
        this.loadInventory();
      },
      error: (error: unknown) => {
        this.operationSubmitting.set(false);
        this.showOperationError(error);
        this.checkOperationStock();
      },
    });
  }

  fulfillmentModeLabel(fulfillmentMode?: string): string {
    return fulfillmentMode === 'PRE_PACKED'
      ? 'package_operation.fulfillment_mode.pre_packed'
      : 'package_operation.fulfillment_mode.virtual_bundle';
  }

  private loadInventory(): void {
    const { page, size } = this.inventoryPage();
    this.inventoryLoading.set(true);
    this.service.inventory(this.currentFilters, page - 1, size).subscribe({
      next: (response) => {
        this.inventoryItems.set(response.items);
        this.inventoryTotalRecords.set(response.totalRecords);
        this.inventoryLoading.set(false);
      },
      error: (error: unknown) => {
        this.inventoryItems.set([]);
        this.inventoryTotalRecords.set(0);
        this.inventoryLoading.set(false);
        this.showLoadError(error, 'package_operation.messages.inventory_load_failed');
      },
    });
  }

  private loadOperationWarehouses(item: PackageInventoryItem): void {
    const currentWarehouses = item.warehouses.map((warehouse) => ({
      label: warehouse.name ? `${warehouse.code} - ${warehouse.name}` : warehouse.code,
      value: warehouse.id,
    }));

    this.warehouseOptionsService
      .getOptionsPage(
        { page: 1, size: 100, searchText: '' },
        { status: true, valueKey: 'id' },
      )
      .subscribe((response) => {
        const options = response.options
          .filter((option): option is OperationWarehouseOption => typeof option.value === 'number')
          .map((option) => ({ label: option.label, value: option.value }));
        const optionsById = new Map<number, OperationWarehouseOption>();
        [...currentWarehouses, ...options].forEach((option) => optionsById.set(option.value, option));
        this.operationWarehouseOptions.set([...optionsById.values()]);
      });
  }

  private loadEligibleSourcePacks(): void {
    const warehouseId = this.operationForm.controls.warehouseId.value;
    const packageSkuCode = this.operationPackage()?.skuCode;
    if (!warehouseId || !packageSkuCode) {
      return;
    }

    this.sourcePacksLoading.set(true);
    this.service.eligibleSourcePacks(warehouseId, packageSkuCode).subscribe({
      next: (sourcePacks) => {
        this.sourcePacks.set(sourcePacks);
        this.sourcePacksLoading.set(false);
      },
      error: () => {
        this.sourcePacks.set([]);
        this.sourcePacksLoading.set(false);
      },
    });
  }

  private checkOperationStock(): void {
    const type = this.operationType();
    const packageSku = this.operationPackage();
    const warehouseId = this.operationForm.controls.warehouseId.value;
    const packageQty = this.operationForm.controls.packageQty.value;

    this.stockCheckRequestId += 1;
    const requestId = this.stockCheckRequestId;
    if (!type || !packageSku || !warehouseId) {
      this.operationPreviewItems.set([]);
      this.operationStockEnough.set(null);
      this.operationValidationMessage.set(null);
      this.operationStockChecking.set(false);
      return;
    }

    if (!this.isWholePositiveQuantity(packageQty)) {
      this.operationStockEnough.set(null);
      this.operationValidationMessage.set(null);
      this.operationStockChecking.set(false);
      return;
    }

    const sourcePack = this.selectedSourcePack();
    if (type === 'UNPACK') {
      if (!sourcePack) {
        this.operationPreviewItems.set([]);
        this.operationStockEnough.set(false);
        this.operationValidationMessage.set('package_operation.messages.source_pack_required');
        return;
      }

      if ((sourcePack.remainingUnpackQty ?? 0) < packageQty) {
        this.operationPreviewItems.set([]);
        this.operationStockEnough.set(false);
        this.operationValidationMessage.set('package_operation.messages.unpack_qty_exceeds_source');
        return;
      }
    }

    this.operationStockChecking.set(true);
    this.operationValidationMessage.set(null);
    this.service.checkStock(warehouseId, packageSku.skuCode, packageQty).subscribe({
      next: (response) => {
        if (requestId !== this.stockCheckRequestId) {
          return;
        }
        const result = response.packages?.find(
          (item) => item.packageSkuCode === packageSku.skuCode,
        );
        this.applyStockCheckResult(type, packageQty, result, sourcePack);
        this.operationStockChecking.set(false);
      },
      error: () => {
        if (requestId !== this.stockCheckRequestId) {
          return;
        }
        this.operationPreviewItems.set([]);
        this.operationStockEnough.set(false);
        this.operationValidationMessage.set('package_operation.messages.stock_check_failed');
        this.operationStockChecking.set(false);
      },
    });
  }

  private applyStockCheckResult(
    type: PackageOperationType,
    packageQty: number,
    result: PackageStockResultVO | undefined,
    sourcePack: PackageOperationVO | undefined,
  ): void {
    if (!result) {
      this.operationPreviewItems.set([]);
      this.operationStockEnough.set(false);
      this.operationValidationMessage.set('package_operation.messages.stock_check_failed');
      return;
    }

    const sourceLinesBySku = new Map(
      (sourcePack?.lines ?? []).map((line) => [line.skuCode, line]),
    );
    const previewItems = (result.components ?? []).map((component) => {
      const currentAvailableQty = component.availableQty ?? 0;
      const quantityChange =
        type === 'PACK'
          ? component.requiredQty ?? (component.requiredQtyPerPackage ?? 0) * packageQty
          : (sourceLinesBySku.get(component.skuCode)?.qtyPerPackage ??
              component.requiredQtyPerPackage ??
              0) * packageQty;
      const availableQtyAfterOperation =
        type === 'PACK'
          ? Math.max(0, currentAvailableQty - quantityChange)
          : currentAvailableQty + quantityChange;

      return {
        skuCode: component.skuCode ?? '-',
        skuName: component.skuName ?? '-',
        qtyPerPackage:
          component.requiredQtyPerPackage ??
          sourceLinesBySku.get(component.skuCode)?.qtyPerPackage ??
          0,
        currentAvailableQty,
        availableQtyAfterOperation,
        stockEnough:
          type === 'UNPACK' ||
          (component.stockEnough !== false && currentAvailableQty >= quantityChange),
      };
    });
    const stockEnough =
      result.stockEnough !== false && previewItems.every((item) => item.stockEnough);

    this.operationPreviewItems.set(previewItems);
    this.operationStockEnough.set(stockEnough);
    this.operationValidationMessage.set(
      stockEnough ? null : 'package_operation.messages.stock_not_enough',
    );
  }

  private selectedSourcePack(): PackageOperationVO | undefined {
    const sourcePackOperationId = this.operationForm.controls.sourcePackOperationId.value;
    return this.sourcePacks().find((sourcePack) => sourcePack.id === sourcePackOperationId);
  }

  private isWholePositiveQuantity(value: number | null): value is number {
    return value !== null && Number.isInteger(value) && value > 0;
  }

  private createOperationNo(type: PackageOperationType): string {
    const cryptoApi = globalThis.crypto;
    const suffix = cryptoApi?.randomUUID?.() ?? this.createSecureOperationSuffix(cryptoApi);
    return `BO-${type}-${suffix}`;
  }

  private createSecureOperationSuffix(cryptoApi: Crypto | undefined): string {
    if (!cryptoApi?.getRandomValues) {
      throw new Error('Cryptographically secure random values are unavailable.');
    }

    const randomValues = new Uint32Array(4);
    cryptoApi.getRandomValues(randomValues);
    return Array.from(randomValues, (value) => value.toString(16).padStart(8, '0')).join('');
  }

  private loadHistory(): void {
    const packageSku = this.selectedPackage();
    if (!packageSku || packageSku.warehouses.length === 0) {
      this.historyItems.set([]);
      return;
    }

    this.historyLoading.set(true);
    this.service
      .historyForPackage(
        packageSku.skuCode,
        packageSku.warehouses.map((warehouse) => warehouse.id),
      )
      .subscribe({
      next: (operations) => {
        this.historyItems.set(operations);
        this.historyLoading.set(false);
      },
      error: (error: unknown) => {
        this.historyItems.set([]);
        this.historyLoading.set(false);
        this.showLoadError(error, 'package_operation.messages.history_load_failed');
      },
      });
  }

  private toOptionalString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }

  private showLoadError(error: unknown, detailKey: string): void {
    const status = (error as { status?: number }).status;
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant(
        status === 403 ? 'package_operation.messages.not_authorized' : 'common.messages.error',
      ),
      detail: this.translateService.instant(
        status === 403
          ? 'package_operation.messages.warehouse_not_authorized'
          : detailKey,
      ),
    });
  }

  private showOperationError(error: unknown): void {
    const status = (error as { status?: number }).status;
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant(
        status === 403 ? 'package_operation.messages.not_authorized' : 'common.messages.error',
      ),
      detail: this.translateService.instant(
        status === 403
          ? 'package_operation.messages.warehouse_not_authorized'
          : 'package_operation.messages.operation_failed',
      ),
    });
  }
}
